import ModalSectionHeader from "@/components/ui/ModalSectionHeader";
import SelectSupplierModal from "@/components/supplier/SelectSupplierModal";
import { MaterialFileFormFields } from "@/components/commonFormItems/MaterialFileFormFields";
import { CURRENCY_OPTIONS, SUPPLIER_STAGE } from "@/constants";
import { useModalBehavior } from "@/hooks/behaviors";
import { SupplierUserSimpleListItem } from "@/types/apis";
import { getExchangeRate } from "@/apis/sap.api";
import {
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  DatePicker,
  Space,
} from "antd";
import { useState, useEffect } from "react";
import type { PurchaseContractFormValues } from "../type";
import styles from "@/asserts/styles/formSection.module.less";
import Big from "big.js";
import { useFormEditable } from "../hooks/useFormEditable";
import { HkdPriceLabel } from "@/components/ui/HkdPriceLabel/HkdPriceLabel";
import { useTranslation } from "react-i18next";

export function BasicContractFormFields() {
  const { t } = useTranslation("contractManagement");
  const form = Form.useFormInstance<PurchaseContractFormValues>();
  const { isBasicContractFormFieldsDisabled } = useFormEditable();
  const {
    open: supplierModalOpen,
    openModal: openSupplierModal,
    closeModal: closeSupplierModal,
  } = useModalBehavior();

  const inputAmount = Form.useWatch("inputAmount", form);
  const currency = Form.useWatch("currency", form);
  const [hkdAmount, setHkdAmount] = useState<string | null>(null);

  useEffect(() => {
    const calculateHkd = async () => {
      if (inputAmount && currency && currency !== "HKD") {
        try {
          const res = await getExchangeRate(currency, "HKD");
          const rate = res.data.data.toString();
          const amountHkd = new Big(inputAmount || 0)
            .times(new Big(rate || 0))
            .round(2)
            .toString();
          setHkdAmount(amountHkd);
          form.setFieldsValue({ rate, amountHkd });
        } catch {
          setHkdAmount(null);
          form.setFieldsValue({ rate: undefined, amountHkd: undefined });
        }
      } else if (currency === "HKD") {
        setHkdAmount(inputAmount);
        form.setFieldsValue({ rate: "1", amountHkd: inputAmount || "" });
      } else {
        setHkdAmount(null);
        form.setFieldsValue({ rate: undefined, amountHkd: undefined });
      }
    };
    calculateHkd();
  }, [inputAmount, currency, form]);

  const onSelectSupplierModalClose = (
    pickedSupplier?: SupplierUserSimpleListItem,
  ) => {
    if (pickedSupplier) {
      form.setFieldsValue({
        supplierId: pickedSupplier.id,
        supplierCode: pickedSupplier.supplierCode,
        supplierNameZh: pickedSupplier.supplierName,
        supplierNameEn: pickedSupplier.supplierUsName,
      });
    }
    closeSupplierModal();
  };

  return (
    <div className={styles.formSection}>
      <ModalSectionHeader title={t("合同基础信息")} />

      <div className={styles.sectionBody}>
        {/* 供应商信息 */}
        <Row gutter={49}>
          <Col span={8}>
            <Form.Item<PurchaseContractFormValues> name="supplierId" hidden />

            <Form.Item<PurchaseContractFormValues>
              name="supplierCode"
              label={t("供应商编号")}
              rules={[
                {
                  required: true,
                  message: t("请选择供应商"),
                },
              ]}
            >
              <Input
                placeholder={t("请选择供应商")}
                readOnly
                onClick={openSupplierModal}
                disabled={isBasicContractFormFieldsDisabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item<PurchaseContractFormValues>
              name="supplierNameZh"
              label={t("供应商名称中文")}
            >
              <Input
                placeholder={t("请选择供应商")}
                readOnly
                onClick={openSupplierModal}
                disabled={isBasicContractFormFieldsDisabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item<PurchaseContractFormValues>
              name="supplierNameEn"
              label={t("供应商名称英文")}
            >
              <Input
                placeholder={t("自动带出")}
                readOnly
                disabled={isBasicContractFormFieldsDisabled}
                onClick={openSupplierModal}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 合同基础信息 */}
        <Row gutter={49}>
          <Col span={8}>
            <Form.Item<PurchaseContractFormValues>
              name="contractName"
              label={t("合同名称")}
              rules={[
                {
                  required: true,
                  message: t("请输入合同名称"),
                },
              ]}
            >
              <Input
                placeholder={t("请输入")}
                disabled={isBasicContractFormFieldsDisabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item<PurchaseContractFormValues>
              name="contractNumber"
              label={t("合同编号")}
              rules={[
                {
                  required: true,
                  message: t("请输入合同编号"),
                },
              ]}
            >
              <Input
                placeholder={t("请输入")}
                disabled={isBasicContractFormFieldsDisabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label={t("合同总金额币种")}>
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item
                  name="inputAmount"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t("请输入合同金额"),
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "60%" }}
                    placeholder={t("请输入")}
                    disabled={isBasicContractFormFieldsDisabled}
                    min={0}
                    precision={2}
                  />
                </Form.Item>
                <Form.Item
                  name="currency"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t("请选择币种"),
                    },
                  ]}
                >
                  <Select
                    style={{ width: "40%" }}
                    options={CURRENCY_OPTIONS}
                    placeholder={t("请选择")}
                    disabled={isBasicContractFormFieldsDisabled}
                  />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <HkdPriceLabel priceHkd={hkdAmount} />
          </Col>
        </Row>

        <Row gutter={49}>
          <Col span={8}>
            <Form.Item<PurchaseContractFormValues>
              name="contractValidity"
              label={t("合同有效期")}
              rules={[
                {
                  required: true,
                  message: t("请选择合同有效期"),
                },
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                disabled={isBasicContractFormFieldsDisabled}
              />
            </Form.Item>
          </Col>

          {/* 隐藏字段 */}
          <Form.Item<PurchaseContractFormValues> name="rate" hidden />
          <Form.Item<PurchaseContractFormValues> name="amountHkd" hidden />
        </Row>

        {/* 合同内容 */}
        <Row gutter={49}>
          <Col span={24}>
            <Form.Item<PurchaseContractFormValues>
              name="contractContent"
              label={t("合同主要内容")}
              rules={[
                {
                  required: true,
                  message: t("请输入合同主要内容"),
                },
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder={t("请输入")}
                disabled={isBasicContractFormFieldsDisabled}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 附件 */}
        <Row gutter={49}>
          <Col span={24}>
            <MaterialFileFormFields
              fieldName="contractFiles"
              label={t("合同文件")}
              bindFlow={true}
              accept=".pdf"
              maxCount={1}
              isCustomDisabled
              disabled={isBasicContractFormFieldsDisabled}
              hiddenRemove={isBasicContractFormFieldsDisabled}
            />
          </Col>

          <Col span={24}>
            <MaterialFileFormFields
              fieldName="relatedFiles"
              label={t("相关文件")}
              required={false}
              bindFlow={true}
              isCustomDisabled
              disabled={isBasicContractFormFieldsDisabled}
              hiddenRemove={isBasicContractFormFieldsDisabled}
            />
          </Col>
        </Row>
      </div>

      <SelectSupplierModal
        open={supplierModalOpen}
        onClose={onSelectSupplierModalClose}
        fixedSupplierStage={SUPPLIER_STAGE.ONBOARDING}
      />
    </div>
  );
}
